import { useQuery } from "@tanstack/react-query";

import { PREDEPOSIT_CLOSED_TRANSITIONED_CACHE_KEY } from "@/constants/local-storage-keys";
import { predepositPackage, readDexSdk } from "@/contexts/dex-sdk";
import { getClosedCache, setClosedCache } from "@/hooks/storage/predeposit-closed-cache";
import { getPredepositStateAddr } from "@/utils/predeposit";

const PREDEPOSIT_TRANSITIONED_QUERY_KEY = "predeposit-transitioned";

function predepositTransitionedQueryKey(userAddress: string) {
  return [PREDEPOSIT_TRANSITIONED_QUERY_KEY, userAddress] as const;
}

async function fetchHasDepositorTransitioned(userAddress: string): Promise<boolean> {
  if (!userAddress) return false;

  const cached = getClosedCache(PREDEPOSIT_CLOSED_TRANSITIONED_CACHE_KEY, userAddress) as
    | boolean
    | undefined;
  if (cached !== undefined) return cached;

  const predepositStateAddr = getPredepositStateAddr(predepositPackage);

  try {
    const [hasTransitioned] = await readDexSdk.deps.aptos.view<[boolean]>({
      payload: {
        function: `${predepositPackage}::predeposit::has_depositor_transitioned`,
        typeArguments: [],
        functionArguments: [predepositStateAddr, userAddress],
      },
    });

    setClosedCache(PREDEPOSIT_CLOSED_TRANSITIONED_CACHE_KEY, hasTransitioned, userAddress);
    return hasTransitioned;
  } catch {
    return false;
  }
}

export function usePredepositTransitioned(userAddress: string | undefined) {
  return useQuery({
    queryKey: predepositTransitionedQueryKey(userAddress ?? ""),
    queryFn: () => fetchHasDepositorTransitioned(userAddress ?? ""),
    enabled: !!userAddress,
    staleTime: Infinity,
  });
}
